import { useExtendedIntl } from 'hooks/useExtendedIntl';
import { useSnackbar } from 'notistack';
import { ReactNode, useCallback } from 'react';
import { VIDEO_NOTIFICATION_TOAST_DURATION_MS } from './constants';
import { VideoGenerationToast, VideoGenerationToastVariant } from './VideoGenerationToast';

interface VideoGenerationToastOptions {
  key?: string;
  /** Receives a function that closes this toast. */
  action?: (close: () => void) => ReactNode;
}

const MESSAGE_IDS = {
  generating: {
    title: 'video-notifications.toast.generating.title',
    description: 'video-notifications.toast.generating.description',
  },
  ready: {
    title: 'video-notifications.toast.generation-ready.title',
    description: 'video-notifications.toast.generation-ready.description',
  },
  failed: {
    title: 'video-notifications.toast.generation-failed.title',
    description: 'video-notifications.toast.generation-failed.description',
  },
} as const satisfies Record<VideoGenerationToastVariant, { title: string; description: string }>;

/**
 * Shows a video generation toast. The strings are translated here, because the toast body renders above
 * `LocalizedIntlProvider` and can't translate them itself.
 */
export function useVideoGenerationToast(): (
  variant: VideoGenerationToastVariant,
  options?: VideoGenerationToastOptions,
) => void {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { formatMessage } = useExtendedIntl();

  return useCallback(
    (variant, options = {}) => {
      const title = formatMessage({ id: MESSAGE_IDS[variant].title });
      const description = formatMessage({ id: MESSAGE_IDS[variant].description });
      const closeLabel = formatMessage({ id: 'shared.close' });

      enqueueSnackbar('', {
        key: options.key ?? `video-generation-${variant}`,
        preventDuplicate: true,
        autoHideDuration: VIDEO_NOTIFICATION_TOAST_DURATION_MS,
        content: (snackbarKey) => {
          const close = () => closeSnackbar(snackbarKey);

          return (
            <VideoGenerationToast
              variant={variant}
              title={title}
              description={description}
              closeLabel={closeLabel}
              onClose={close}
              action={options.action?.(close)}
            />
          );
        },
      });
    },
    [closeSnackbar, enqueueSnackbar, formatMessage],
  );
}
